import { Helmet } from "react-helmet-async";

import { PageHeader } from "@/components/shared/PageHeader";
import { COMPANY, getEmailLink } from "@/config/company";

export default function PrivacyPolicy() {
  const lastUpdated = "January 29, 2026";

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Privacy Policy | {COMPANY.name}</title>
        <meta 
          name="description" 
          content={`Strict Privacy Policy for ${COMPANY.name}. Comprehensive details on data collection, PHI handling, AI processing, and international data transfers.`} 
        />
        <link rel="canonical" href={`${COMPANY.website}/privacy-policy/`} />
        <meta property="og:title" content={`Privacy Policy | ${COMPANY.name}`} />
        <meta property="og:description" content={`Strict Privacy Policy for ${COMPANY.name}. Comprehensive details on data collection, PHI handling, AI processing, and international data transfers.`} />
        <meta property="og:url" content={`${COMPANY.website}/privacy-policy/`} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={`Privacy Policy | ${COMPANY.name}`} />
        <meta name="twitter:description" content={`Strict Privacy Policy for ${COMPANY.name}. Comprehensive details on data collection, PHI handling, AI processing, and international data transfers.`} />
        <meta name="twitter:url" content={`${COMPANY.website}/privacy-policy/`} />
      </Helmet>



      <main className="pt-24">
        <PageHeader
          title="Privacy Policy"
          description={`Last Updated: ${lastUpdated}`}
          className="mb-10"
        />

        <div className="container mx-auto px-4 pb-20">
          <div className="max-w-4xl mx-auto prose prose-lg dark:prose-invert">
            
            {/* LEGAL DISCLAIMER ALERT */}
            <div className="bg-amber-50 dark:bg-amber-950/30 border-l-4 border-amber-500 p-4 my-6">
              <p className="font-bold text-amber-700 dark:text-amber-400 m-0">LEGAL DISCLAIMER</p>
              <p className="text-amber-800 dark:text-amber-300 text-sm mt-1 mb-0">
                This Privacy Policy is a legally binding document between you and {COMPANY.name}. 
                It governs your use of our Platform and services. 
                By accessing or using the Platform, you acknowledge that you have read, understood, and agree to be bound by this Policy. 
                IF YOU DO NOT AGREE, DO NOT USE THE PLATFORM.
              </p>
            </div>

            <p className="lead">
              <strong>{COMPANY.name}</strong> ("we," "our," or "us"), doing business as TrustedHeal India, operates the website 
              and associated AI-powered services (collectively, the "Platform"). 
              We are committed to protecting your privacy with the highest standards of security, specifically regarding 
              <strong> Protected Health Information (PHI)</strong> and <strong>Sensitive Personal Data</strong>.
            </p>

            <p>
              This Policy has been drafted in strict compliance with the <strong>Digital Personal Data Protection Act, 2023 (India)</strong>, 
              the <strong>Information Technology Act, 2000</strong>, and where applicable, principles aligned with <strong>HIPAA (USA)</strong> and <strong>GDPR (EU/UK)</strong> 
              for international data transfers.
            </p>

            <h2>1. Explicit Consent & Data Collection</h2>
            <p>
              By voluntarily providing your data, you grant us explicit, unequivocal consent to collect, process, and store the following categories of information:
            </p>
            <ul>
              <li>
                <strong>Personal Identity Information (PII):</strong> Full name, passport details, age, gender, nationality, and government-issued ID proof.
              </li>
              <li>
                <strong>Sensitive Medical Data (PHI):</strong> Existing medical conditions, past surgeries, diagnostic reports (DICOM, PDF, Images), prescriptions, physician notes, and treatment preferences.
              </li>
              <li>
                <strong>Communication Data:</strong> Email correspondence, WhatsApp chat logs, and recordings of calls with our voice agents (processed for quality and AI training purposes).
              </li>
              <li>
                <strong>Technical & Behavioral Data:</strong> IP address, device fingerprint, browser geolocation, and interaction logs with our AI chatbot/agents.
              </li>
            </ul>

            <h2>2. Use of Artificial Intelligence (AI)</h2>
            <div className="bg-blue-50 dark:bg-blue-950/30 border-l-4 border-blue-500 p-4 my-4">
              <p className="font-bold text-blue-700 dark:text-blue-400 m-0">AI DATA PROCESSING NOTICE</p>
              <p className="text-blue-800 dark:text-blue-300 text-sm mt-1 mb-0">
                You explicitly acknowledge and agree that your Anonymized Data may be processed by third-party Artificial Intelligence models 
                (including but not limited to OpenAI, ElevenLabs, and others) for the purpose of generating responses, summaries, and treatment insights.
              </p>
            </div>
            <p>We use AI technologies to:</p>
            <ul>
              <li>Analyze medical reports to match you with suitable hospitals.</li>
              <li>Provide instant, automated responses via our chatbot and voice agents.</li>
              <li>Translate medical documents and communications.</li>
            </ul>
            <p>
              <strong>De-identification & Risk:</strong> We employ commercial best efforts and industry-standard masking techniques (replacing names/emails) before transmitting data to AI processors. 
              However, you acknowledge the inherent risk that re-identification may technically be possible, and you explicitly consent to this processing risk.
            </p>

            <h2>3. International Data Transfer</h2>
            <p>
              As a medical tourism facilitator based in <strong>India</strong>, your data explicitly must be transferred outside your home country 
              (e.g., USA, UK, Canada, UAE) to India.
            </p>
            <p>
              <strong>By using our services, you expressly consent to this cross-border transfer.</strong> 
              You acknowledge that India may have different data protection laws than your country of residence. 
              However, we contractually bind our partner hospitals and service providers to maintain strict confidentiality.
            </p>

            <h2>4. Disclosure to Third Parties</h2>
            <p>
              We are not in the business of selling data. Your strict medical confidentiality is paramount. 
              However, to provide the requested services, we must share specific data with:
            </p>
            <ul>
              <li>
                <strong>Network Hospitals & Doctors:</strong> Your medical records are shared <em>only</em> with the specific providers you choose or browse for the purpose of obtaining a medical opinion/quote.
              </li>
              <li>
                <strong>Government Authorities:</strong> For Medical Visa processing (e-Medical Visa) and compliance with Indian Foreigner Registration Office (FRRO) requirements.
              </li>
              <li>
                <strong>Legal & Safety:</strong> If required by a court order or to prevent an imminent threat to life or safety.
              </li>
            </ul>

            <h2>5. Data Retention & Deletion</h2>
            <p>
              We retain your data only as long as necessary to fulfill the purposes outlined here or as required by law (e.g., maintaining records for 5 years per medical regulations).
            </p>
            <p>
              <strong>Right to Erasure:</strong> You may request the deletion of your personal data at any time by contacting our Data Protection Officer. 
              Note that some data may be retained in encrypted backups for a limited period or if required by law.
            </p>

            <h2>6. Security Measures</h2>
            <p>
              We implement industry-leading security protocols:
            </p>
            <ul>
              <li><strong>End-to-End Encryption:</strong> All data in transit is encrypted using TLS 1.3+.</li>
              <li><strong>At-Rest Encryption:</strong> Database records are encrypted using AES-256 standards.</li>
              <li><strong>Access Control:</strong> Zero-trust architecture restricts access to your data only to assigned Case Managers.</li>
            </ul>

            <h2>7. Children's Privacy</h2>
            <p>
              Our services are not intended for individuals under the age of 18 without parental consent. 
              For pediatric patients, we strictly require explicit written consent from a parent or legal guardian before processing any data.
            </p>

            <h2>8. Changes to Policy</h2>
            <p>
              We reserve the absolute right to modify this policy at our sole discretion. 
              Significant changes will be notified via email or a prominent notice on the Platform. 
              Your continued use of the Platform signifies acceptance of the revised terms.
            </p>


            <h2>9. Cookie Policy & Tracking</h2>
            <p>
              We use specific types of cookies to enhance your experience:
            </p>
            <ul>
              <li><strong>Essential Cookies:</strong> Strictly necessary for the security and operation of the Platform. Cannot be disabled.</li>
              <li><strong>Analytics Cookies:</strong> Used to understand user behavior (e.g., Google Analytics). You may opt-out via your browser settings.</li>
              <li><strong>Marketing Cookies:</strong> Used to deliver relevant advertisements. We require your explicit opt-in for these.</li>
            </ul>

            <h2>10. Data Breach Notification</h2>
            <p>
              In the unlikely event of a Personal Data breach, we are legally committed to:
            </p>
            <ul>
              <li><strong>Notify Authority:</strong> Inform the Data Protection Board of India within 72 hours of becoming aware of the breach.</li>
              <li><strong>Notify Users:</strong> Inform affected users without undue delay, outlining the nature of the breach and remedial measures taken.</li>
            </ul>

            <h2>11. Genetic & Biometric Data</h2>
            <p>
              For specific treatments (e.g., Oncology, Cardiology), we may process <strong>Genetic Data</strong> or <strong>Biometric Data</strong>. 
              We strictly process this data only with your <strong>specific, separate written consent</strong> and in accordance with enhanced security protocols.
            </p>

            <h2>12. Contact & Grievance Officer</h2>
            <p>
              In accordance with the IT Act 2000 and DPDPA 2023, the contact details of the Grievance Officer are provided below:
            </p>
            <p className="bg-muted p-6 rounded-lg border">
              <strong>Grievance Officer</strong><br />
              {COMPANY.name} Legal Team<br />
              <strong>Address:</strong> {COMPANY.address.multiline}<br />
              <strong>Email:</strong> <a href={getEmailLink("support")} className="text-primary hover:underline">{COMPANY.email.support}</a>
            </p>
            
          </div>
        </div>
      </main>


    </div>
  );
}
